import { Component, Input, ChangeDetectionStrategy, forwardRef, ElementRef } from "@angular/core";
import { FormOption, PluralizeService } from 'core'
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms'
import { Observable, BehaviorSubject, Subscription } from "rxjs";
import { combineLatest } from "rxjs/observable/combineLatest";
import { map, shareReplay, filter, take } from "rxjs/operators"

const noop = () => {}

@Component({
  selector: "fast-multiselect",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FastMultiselectComponent),
      multi: true
    }
  ],
  host: {
    '(document:click)': 'onDocumentClick($event)'
  },
  template: require('./fast-multiselect.component.html'),
  styles: [require('./fast-multiselect.component.sass')]
})
export class FastMultiselectComponent implements ControlValueAccessor {
  // options
  private optionsSubject = new BehaviorSubject<FormOption[]>([])
  @Input() set options(options: FormOption[]) {
    this.optionsSubject.next(options)
  }
  readonly options$: Observable<FormOption[]> = this.optionsSubject.asObservable().pipe(
    filter(options => options != null),
    shareReplay(1)
  )
  // open / close
  private isOpenSubject = new BehaviorSubject<boolean>(false)
  readonly isOpen$ = this.isOpenSubject.asObservable()
  // selected values
  private selectedValuesSubject = new BehaviorSubject<any[]>([])
  readonly selectedValues$ = this.selectedValuesSubject.asObservable()
  // caption
  caption$ = combineLatest([this.options$, this.selectedValues$]).pipe(
    map(([options, selectedValues]) => {
      const selectedOptions = options.filter(o => selectedValues.indexOf(o.value) != -1)
      if (selectedOptions.length == 0) {
        return ''
      }
      else if (selectedOptions.length == 1) {
        return selectedOptions[0].label
      } else {
        return this.pluralizeService.instant('xOfTotalSelected', selectedOptions.length, { total: options.length })
      }
    }),
    shareReplay(1)
  )
  // all selected
  allSelected$ = combineLatest([this.options$, this.selectedValues$]).pipe(
    map(([options, selectedValues]) => options.length == selectedValues.length),
    shareReplay(1)
  )
  // subs
  private subs: Subscription[] = []
  // filter options
  private searchTermSubject = new BehaviorSubject<string>("")
  readonly searchTerm$ = this.searchTermSubject.asObservable()
  setSearchTerm(term: string) {
    this.searchTermSubject.next(term.trim().toLocaleLowerCase())
  }
  filteredOptions$ = combineLatest([this.searchTerm$, this.options$]).pipe(
    map(data => {
      const [term, options] = data as [string, FormOption[]]
      return options.filter(o => o.label.toLowerCase().indexOf(term) !== -1)
    }),
    shareReplay(1)
  )
  showSearch$ = this.options$.pipe(map((options: FormOption[]|null) => options && options.length >= 10))

  constructor(
    private pluralizeService: PluralizeService,
    private elRef: ElementRef
  ) {}

  ngOnInit() {
    this.subs.push(
      this.selectedValues$.subscribe(values => {
        this.onChangeCallback(values)
      })
    )
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe())
  }

  open() {
    this.isOpenSubject.next(true)
  }

  close() {
    this.isOpenSubject.next(false)
    this.onTouchedCallback()
    this.setSearchTerm("")
  }

  toggleOpen() {
    if (this.isOpenSubject.getValue()) {
      this.close()
    } else {
      this.open()
    }
  }

  selectAll() {
    const allValues = this.optionsSubject.getValue().map(o => o.value)
    this.selectedValuesSubject.next(allValues)
  }

  deselectAll() {
    this.selectedValuesSubject.next([])
  }

  toggleSelectAll() {
    this.allSelected$.pipe(take(1)).subscribe(allSelected => {
      if (allSelected) {
        this.deselectAll()
      } else {
        this.selectAll()
      }
    })
  }

  selectOption(option: FormOption) {
    const selectedValues = this.selectedValuesSubject.getValue()
    this.selectedValuesSubject.next([...selectedValues, option.value])
  }

  deselectOption(option: FormOption) {
    const selectedValues = this.selectedValuesSubject.getValue()
    this.selectedValuesSubject.next(selectedValues.filter(v => v != option.value))
  }

  writeValue(values: any[]) {
    this.selectedValuesSubject.next(values)
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn
  }

  private onTouchedCallback: () => void = noop

  private onChangeCallback: (_: any) => void = noop

  private onDocumentClick(event: PointerEvent) {
    const isOpen = this.isOpenSubject.getValue()
    if (isOpen && !this.elRef.nativeElement.contains(event.target)) {
      this.close()
    }
  }

  optionsTrackByFn(index: number, item: FormOption) {
    return item.value
  }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./app/common/forms/fast-multiselect/fast-multiselect.component.ts